import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableSortLabel,
} from '@mui/material';
import { useSpecifications, useSeverityLevels } from '@/hooks/use-errors';
import { useTranslation } from 'react-i18next';
import type { SeverityLevel } from '@/models/specification';

interface ErrorsDialogProps {
  open: boolean;
  onClose: () => void;
}

type SortColumn = 'Error' | 'Severity' | 'Description' | 'Problem' | 'Correction';

export function ErrorsDialog({ open, onClose }: ErrorsDialogProps) {
  const { t } = useTranslation('load');
  const [selectedSpecificationId, setSelectedSpecificationId] = useState<number | ''>('');

  // Sorting state (matching Vue app behavior)
  const [sortBy, setSortBy] = useState<SortColumn>('Error');
  const [sortDesc, setSortDesc] = useState(false);

  // Get specifications list
  const {
    data: specifications = [],
    isLoading: specificationsLoading,
    error: specificationsError,
  } = useSpecifications();

  // Get severity levels for selected specification
  const {
    data: severityLevels = [],
    isLoading: severityLevelsLoading,
    error: severityLevelsError,
  } = useSeverityLevels(
    selectedSpecificationId ? Number(selectedSpecificationId) : undefined
  );

  // Loading and error states
  const isLoading = specificationsLoading || severityLevelsLoading;
  const error = specificationsError || severityLevelsError;

  // Auto-select first specification when loaded (matching Vue app behavior)
  useEffect(() => {
    if (specifications.length > 0 && !selectedSpecificationId) {
      setSelectedSpecificationId(specifications[0].Id);
    }
  }, [specifications, selectedSpecificationId]);

  // Sort severity levels (matching Vue app behavior)
  const sortedSeverityLevels = useMemo(() => {
    if (!severityLevels.length) return severityLevels;

    return [...severityLevels].sort((a, b) => {
      // Map sort column to actual property names
      const propertyMap: Record<SortColumn, keyof SeverityLevel> = {
        'Error': 'Code',
        'Severity': 'Description',
        'Description': 'Message',
        'Problem': 'Problem',
        'Correction': 'Correction',
      };

      const property = propertyMap[sortBy];
      // Type guard to ensure property exists on SeverityLevel
      if (!(property in a) || !(property in b)) {
        return 0;
      }
      const aValue = a[property] ?? '';
      const bValue = b[property] ?? '';

      let result = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        result = aValue.localeCompare(bValue);
      } else {
        result = String(aValue).localeCompare(String(bValue));
      }

      return sortDesc ? -result : result;
    });
  }, [severityLevels, sortBy, sortDesc]);

  // Handle column sort (matching Vue app behavior)
  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(column);
      setSortDesc(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth data-testid="errors-dialog">
      <DialogTitle data-testid="errors-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('dialogs.errors.title')}</Typography>
          <Box display="flex" gap={1} alignItems="center">
            {/* Specification Selector */}
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <InputLabel>{t('dialogs.errors.specification')}</InputLabel>
              <Select
                data-testid="errors-dialog-specification-select"
                value={selectedSpecificationId}
                label={t('dialogs.errors.specification')}
                onChange={(e) =>
                  setSelectedSpecificationId(e.target.value as number | '')
                }
                disabled={specificationsLoading}
              >
                <MenuItem value="" data-testid="errors-dialog-specification-option-empty">
                  <em>{t('dialogs.errors.selectSpecification')}</em>
                </MenuItem>
                {specifications.map((spec) => {
                  return (
                    <MenuItem key={spec.Id} value={spec.Id} data-testid={`errors-dialog-specification-option-${spec.Id}`}>
                      {spec.Name || t('dialogs.errors.unnamedSpecification')}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers data-testid="errors-dialog-content">
        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" p={4} data-testid="errors-dialog-loading">
            <CircularProgress data-testid="errors-dialog-loading-spinner" />
            <Typography sx={{ ml: 2 }}>
              {specificationsLoading
                ? t('dialogs.errors.loadingSpecifications')
                : t('dialogs.errors.loadingSeverityLevels')}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('dialogs.errors.failedToLoadData', { message: (error as Error).message })}
          </Alert>
        )}

        {/* No Specification Selected */}
        {!isLoading && !error && !selectedSpecificationId && (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="text.secondary">
              {t('dialogs.errors.selectToViewDetails')}
            </Typography>
          </Box>
        )}

        {/* Empty State - No Severity Levels */}
        {!isLoading &&
          !error &&
          selectedSpecificationId &&
          severityLevels.length === 0 && (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="text.secondary">
                {t('dialogs.errors.noSeverityLevels')}
              </Typography>
            </Box>
          )}

        {/* Severity Levels Table */}
        {!isLoading && !error && selectedSpecificationId && severityLevels.length > 0 && (
          <TableContainer component={Paper} variant="outlined" data-testid="errors-dialog-table-container">
            <Table size="small" data-testid="errors-dialog-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '80px', cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortBy === 'Error'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('Error')}
                    >
                      {t('dialogs.errors.columnError')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '100px', cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortBy === 'Severity'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('Severity')}
                    >
                      {t('dialogs.errors.columnSeverity')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '250px', cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortBy === 'Description'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('Description')}
                    >
                      {t('dialogs.errors.columnDescription')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortBy === 'Problem'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('Problem')}
                    >
                      {t('dialogs.errors.columnProblem')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}>
                    <TableSortLabel
                      active={sortBy === 'Correction'}
                      direction={sortDesc ? 'desc' : 'asc'}
                      onClick={() => handleSort('Correction')}
                    >
                      {t('dialogs.errors.columnCorrection')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSeverityLevels.map((level, index) => (
                  <TableRow key={`${selectedSpecificationId}-${index}`} hover>
                    <TableCell>
                      <Typography variant="body2">{level.Code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{level.Description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{level.Message}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{level.Problem}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{level.Correction}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-testid="errors-dialog-close-button">{t('dialogs.errors.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
